// src/pages/nations/NationTable.tsx

import { useState, useEffect, useRef } from "react";
import { Edit, Trash2, Eye, AlertTriangle, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import TablePagination from "@/components/TablePagination";
import type { Nation, Pagination } from "@/types/api.types";
import type { NationFilters } from "@/api/services/nations.service";

const GROUPS = [
  "Group A", "Group B", "Group C", "Group D",
  "Group E", "Group F", "Group G", "Group H",
  "Group I", "Group J", "Group K", "Group L",
];

interface NationTableProps {
  nations: Nation[];
  isLoading: boolean;
  filters: NationFilters;
  onFiltersChange: (filters: NationFilters) => void;
  onClearFilters: () => void;
  onEdit: (nation: Nation) => void;
  onView: (nation: Nation) => void;
  onDelete: (id: number) => void;
  onEliminate: (nation: Nation) => void;
  pagination?: Pagination;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

export default function NationTable({
  nations,
  isLoading,
  filters,
  onFiltersChange,
  onClearFilters,
  onEdit,
  onView,
  onDelete,
  onEliminate,
  pagination,
  onPageChange,
  onPageSizeChange,
}: NationTableProps) {
  const [localSearch, setLocalSearch] = useState(filters.search || "");
  const filtersRef = useRef(filters);
  const onFiltersChangeRef = useRef(onFiltersChange);
  const isMounted = useRef(false);

  useEffect(() => { filtersRef.current = filters; }, [filters]);
  useEffect(() => { onFiltersChangeRef.current = onFiltersChange; }, [onFiltersChange]);
  useEffect(() => { setLocalSearch(filters.search || ""); }, [filters.search]);

  // Debounce search
  useEffect(() => {
    if (!isMounted.current) { isMounted.current = true; return; }
    const timer = setTimeout(() => {
      onFiltersChangeRef.current({ ...filtersRef.current, search: localSearch || undefined });
    }, 400);
    return () => clearTimeout(timer);
  }, [localSearch]);

  const hasActiveFilters =
    !!filters.search || !!filters.groupName || filters.isEliminated !== undefined;

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Filters + Stats */}
      <div className="p-4 border-b flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-3 items-center">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <Input
              placeholder="Search nations..."
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
              className="pl-8 max-w-xs"
            />
          </div>

          {/* Group filter */}
          <Select
            value={filters.groupName || "all"}
            onValueChange={(val) =>
              onFiltersChange({ ...filters, groupName: val === "all" ? undefined : val })
            }
          >
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Group" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Groups</SelectItem>
              {GROUPS.map((g) => (
                <SelectItem key={g} value={g}>{g}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Status filter */}
          <Select
            value={filters.isEliminated === undefined ? "all" : String(filters.isEliminated)}
            onValueChange={(val) =>
              onFiltersChange({
                ...filters,
                isEliminated: val === "all" ? undefined : val === "true",
              })
            }
          >
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="false">Active</SelectItem>
              <SelectItem value="true">Eliminated</SelectItem>
            </SelectContent>
          </Select>

          {/* Clear filters */}
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearFilters}
              className="text-gray-500 hover:text-gray-700 gap-1"
            >
              <X className="h-3.5 w-3.5" />
              Clear
            </Button>
          )}
        </div>

        <div className="flex items-center gap-2 text-xs">
          <Badge className="bg-gray-100 text-gray-700 font-medium">{pagination?.total ?? nations.length} Total</Badge>
          <Badge className="bg-green-100 text-green-700 font-medium">{nations.filter((n) => !n.isEliminated).length} Active</Badge>
          <Badge className="bg-red-100 text-red-700 font-medium">{nations.filter((n) => n.isEliminated).length} Eliminated</Badge>
        </div>
      </div>

      {/* Table */}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nation</TableHead>
            <TableHead>Code</TableHead>
            <TableHead>Group</TableHead>
            <TableHead>Players</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-16">
                <div className="flex justify-center">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
                </div>
              </TableCell>
            </TableRow>
          ) : nations.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                No nations found
              </TableCell>
            </TableRow>
          ) : (
            nations.map((nation) => (
              <TableRow
                key={nation.id}
                className={nation.isEliminated ? "bg-red-50" : ""}
              >
                <TableCell>
                  <div className="flex items-center gap-3">
                    {nation.flagUrl ? (
                      <img
                        src={nation.flagUrl}
                        alt={nation.name}
                        className="w-10 h-7 rounded object-cover border"
                      />
                    ) : (
                      <div className="w-10 h-7 bg-gray-200 rounded flex items-center justify-center text-xs text-gray-500">
                        {nation.code}
                      </div>
                    )}
                    <div className="font-medium">{nation.name}</div>
                  </div>
                </TableCell>
                <TableCell>
                  <code className="text-sm bg-gray-100 px-2 py-1 rounded font-mono">
                    {nation.code}
                  </code>
                </TableCell>
                <TableCell>
                  {nation.groupName ? (
                    <Badge variant="outline" className="font-mono">
                      {nation.groupName}
                    </Badge>
                  ) : (
                    <span className="text-gray-400 text-sm">—</span>
                  )}
                </TableCell>
                <TableCell>
                  <span className="text-sm">{nation._count?.players || 0} players</span>
                </TableCell>
                <TableCell>
                  {nation.isEliminated ? (
                    <Badge variant="destructive" className="bg-red-600">Eliminated</Badge>
                  ) : (
                    <Badge variant="success" className="bg-green-600 text-white">Active</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => onView(nation)} title="View">
                      <Eye className="h-4 w-4" />
                    </Button>
                    {!nation.isEliminated && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => onEdit(nation)} title="Edit">
                          <Edit className="h-4 w-4 text-blue-600" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => onEliminate(nation)} title="Eliminate">
                          <AlertTriangle className="h-4 w-4 text-orange-600" />
                        </Button>
                      </>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => onDelete(nation.id)} title="Delete">
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {pagination && (
        <TablePagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          totalItems={pagination.total}
          pageSize={pagination.limit}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      )}
    </div>
  );
}
